import { Icon } from '@aibees/shared/components';
import { useBoolean, useNotify } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { IconButton, Tooltip } from '@mui/material';
import { memo } from 'react';
import { useDeletePaymentCardMutationApi } from '../../api/use-delete-payment-card-mutation-api';
import { PaymentConfirmDialog } from '../payment-confirm-dialog';
import type { RemovePaymentCardProps as Props } from './types';

const TOOLTIP_COPY = {
  activeOnly:
    'Cannot delete. This card is being used for an active subscription. Add another card and set it as default first.',
  defaultProtected:
    'Cannot delete. This is your default payment method. Set another card as default first.',
};

export const RemovePaymentCard = memo(
  ({ className, item, isPaidPlan, hasAnyDefault }: Props) => {
    const notify = useNotify();
    const dialog = useBoolean();

    const api = useDeletePaymentCardMutationApi(item.id);

    const inUse = !!item.inUse;
    const isDefault = !!item.isDefault;

    const disabledReason: keyof typeof TOOLTIP_COPY | null = !isPaidPlan
      ? null
      : isDefault
      ? 'defaultProtected'
      : inUse && !hasAnyDefault
      ? 'activeOnly'
      : null;
    const disabled = disabledReason !== null;

    const variant =
      isPaidPlan && inUse && hasAnyDefault
        ? 'deleteWithDefaultFallback'
        : 'deleteGeneric';

    const handleClose = () => {
      dialog.onFalse();
    };

    const handleOpen = () => {
      if (disabled) return;
      dialog.onTrue();
    };

    const handleRemove = () => {
      api.mutateAsync({}).then(() => {
        notify.success('Card Successfully removed');
        handleClose();
      });
    };

    const trash = (
      <IconButton
        className={classnames('RemovePaymentCard-trigger', className)}
        size="xsmall"
        color="error"
        variant="outlined"
        onClick={handleOpen}
        disabled={disabled}
      >
        <Icon name="delete" size={18} />
      </IconButton>
    );

    return (
      <>
        {disabledReason ? (
          <Tooltip title={TOOLTIP_COPY[disabledReason]} placement="left">
            <span>{trash}</span>
          </Tooltip>
        ) : (
          trash
        )}

        <PaymentConfirmDialog
          className={classnames('RemovePaymentCard', className)}
          open={dialog.value}
          variant={variant}
          loading={api.isLoading}
          card={item.card}
          onConfirm={handleRemove}
          onClose={handleClose}
          iconType={item.iconType}
        />
      </>
    );
  }
);

RemovePaymentCard.displayName = 'RemovePaymentCard';
